import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { createPortal } from "react-dom";

/**
 * CRMStockReport
 *
 * Fetches: `${baseUrl}/api/CRMStockReport/${companyCode}/STK_LOC_QTY_COST_ASON/${endDate}/-/-/-/-/TOTALS/${locationParameter}/1/1000`
 */

const DEFAULT_BASE_URL = "";

const COLUMNS = [
  { key: "CODE", label: "Item Code", align: "left", mono: true },
  { key: "DESCRIPTION", label: "Description", align: "left", wrap: true },
  { key: "GROUP", label: "Group", align: "left" },
  { key: "SUBGROUP", label: "Subgroup", align: "left" },
  { key: "CATEGORY", label: "Category", align: "left" },
  { key: "SUBCATEG", label: "Subcategory", align: "left" },
  { key: "OEM", label: "OEM", align: "left" },
  { key: "LOCBIN", label: "Loc/Bin", align: "left", mono: true },
  { key: "QTY", label: "Qty", align: "right", mono: true, decimals: 2 },
  {
    key: "COST",
    label: "Unit Cost",
    align: "right",
    mono: true,
    decimals: 3,
    currency: true,
  },
  {
    key: "TOTAL",
    label: "Total Value",
    align: "right",
    mono: true,
    decimals: 5,
    currency: true,
  },
];

function formatCellValue(value, col) {
  if (value === null || value === undefined || value === "") return "—";

  if (
    typeof value === "number" ||
    (col.decimals !== undefined && !isNaN(Number(value)))
  ) {
    const n = Number(value);
    const formatted = n.toLocaleString(undefined, {
      minimumFractionDigits: col.decimals ?? 0,
      maximumFractionDigits: col.decimals ?? 2,
    });
    return col.currency ? formatted : formatted;
  }
  return String(value);
}

function todayISO() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default function StockReportLocationwise({
  baseUrl,
  apiLocationList,
  company_code_from_local_storage,
}) {
  const [companyCode, setCompanyCode] = useState("");
  
  // UI input states
  const [endDate, setEndDate] = useState(todayISO());
  const [locationParameter, setLocationParameter] = useState(
    apiLocationList ? apiLocationList[0]?.loc_code : "-"
  );
  
  // Request trigger states
  const [appliedFilters, setAppliedFilters] = useState({
    endDate: todayISO(),
    locationParameter: apiLocationList ? apiLocationList[0]?.loc_code : "-",
  });

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [locationOpen, setLocationOpen] = useState(false);
  const [locationPos, setLocationPos] = useState({ top: 0, left: 0, width: 0 });
  const locationBtnRef = useRef(null);
  const locationListRef = useRef(null);

  const openLocationDropdown = () => {
    const rect = locationBtnRef.current?.getBoundingClientRect();
    if (rect) {
      setLocationPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    setLocationOpen((o) => !o);
  };

  useEffect(() => {
    function handleClickOutside(e) {
      const inButton = locationBtnRef.current?.contains(e.target);
      const inList = locationListRef.current?.contains(e.target);
      if (!inButton && !inList) setLocationOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!locationOpen) return;
    function reposition() {
      const rect = locationBtnRef.current?.getBoundingClientRect();
      if (rect) {
        setLocationPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
      }
    }
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [locationOpen]);

  useEffect(() => {
    try {
      setCompanyCode(company_code_from_local_storage);
    } catch (e) {
      // localStorage unavailable
    }
  }, []);

  const buildUrl = useCallback(() => {
    const loc = appliedFilters.locationParameter || "-";
    return `${baseUrl}/api/CRMStockReport/${encodeURIComponent(
      companyCode
    )}/STK_LOC_QTY_COST_ASON/${encodeURIComponent(
      appliedFilters.endDate
    )}/-/-/-/-/TOTALS/${encodeURIComponent(loc)}/1/1000`;
  }, [baseUrl, companyCode, appliedFilters]);

  const fetchReport = useCallback(async () => {
    if (!companyCode) {
      setError("No company code found in local storage.");
      setRows([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(buildUrl());
      if (!res.ok) {
        throw new Error(`Request failed with status ${res.status}`);
      }
      const json = await res.json();

      let data = [];
      let summaryResult = null;

      const isSummaryRow = (obj) =>
        obj &&
        typeof obj === "object" &&
        ("Total_Qty" in obj ||
          "Total_Value" in obj ||
          "TotalRows" in obj ||
          "LastPage" in obj);

      if (Array.isArray(json) && json.length > 0 && isSummaryRow(json[0])) {
        summaryResult = json[0];
      } else if (Array.isArray(json)) {
        data = json;
      } else if (json && Array.isArray(json.data)) {
        data = json.data;
      } else if (json && Array.isArray(json.result)) {
        data = json.result;
      } else if (json && isSummaryRow(json)) {
        summaryResult = json;
      } else if (json && typeof json === "object") {
        data = [json];
      }

      setRows(data);
      setSummary(summaryResult);
    } catch (err) {
      setError(err.message || "Failed to load stock report.");
      setRows([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [buildUrl, companyCode]);

  useEffect(() => {
    fetchReport();
  }, [appliedFilters, companyCode]);

  // Triggered instantly when the HTML calendar date picker changes
  const handleDateChange = (e) => {
    const nextDate = e.target.value;
    setEndDate(nextDate);
    
    // Update appliedFilters immediately to trigger the API fetch hook
    setAppliedFilters((prev) => ({
      ...prev,
      endDate: nextDate,
    }));
  };

  // Triggered instantly when selecting a location dropdown value
  const handleLocationSelect = (locCode) => {
    const nextLoc = locCode || "-";
    setLocationParameter(nextLoc);
    
    // Update appliedFilters immediately to trigger the API fetch hook
    setAppliedFilters((prev) => ({
      ...prev,
      locationParameter: nextLoc,
    }));
    setLocationOpen(false);
  };

  // Kept for manual user clicks / refresh behavior
  const handleApplyFilters = () => {
    setAppliedFilters({
      endDate: endDate,
      locationParameter: locationParameter || "-"
    });
  };

  return (
    <div
      style={{
        "--ink": "#152238",
        "--paper": "#F5F6F8",
        "--surface": "#FFFFFF",
        "--line": "#DCE1E8",
        "--accent": "#0F6E5F",
        "--accent-soft": "#E4F2EE",
        "--muted": "#5B6472",
      }}
      className="w-full text-[var(--ink)] font-sans"
    >
      <style>{`
        .mono { font-family: "IBM Plex Mono", "SFMono-Regular", Menlo, Consolas, monospace; font-variant-numeric: tabular-nums; }
        .stock-table th, .stock-table td { border-bottom: 1px solid var(--line); }
        .stock-table tbody tr:hover { background: var(--accent-soft); }
        .stock-table tbody tr:nth-child(even) { background: rgba(21,34,56,0.015); }
      `}</style>

      <div className="max-w-7xl mx-auto px-2 pt-4 pb-2">
        <header className="flex items-baseline justify-between mb-1 pb-1 border-[var(--line)]">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">
              Stock Locationwise
            </p>
          </div>
        </header>
        
        <div className="mb-6 bg-[var(--surface)] border border-[var(--line)] rounded-lg p-4">
          <div className="flex flex-col sm:flex-row sm:flex-nowrap items-stretch sm:items-end gap-3 sm:gap-4 mb-6 bg-[var(--surface)] border border-[var(--line)] rounded-lg p-3 sm:p-4 sm:overflow-x-auto">
            <div className="flex flex-row flex-nowrap items-end gap-2 sm:contents">
              
              {/* Date Input */}
              <div className="flex flex-col gap-1 flex-1 min-w-0 sm:flex-none sm:shrink-0">
                <label className="text-xs font-medium text-[var(--muted)]" htmlFor="endDate">
                  As-on date
                </label>
                <input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={handleDateChange}
                  className="w-full min-w-0 sm:w-auto border border-[var(--line)] rounded-md px-2 py-1.5 sm:px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                />
              </div>

              {/* Location Dropdown */}
              <div className="flex flex-col gap-1 shrink-0">
                <label className="text-xs font-medium text-[var(--muted)]" htmlFor="location">
                  Location
                </label>
                <button
                  id="location"
                  ref={locationBtnRef}
                  type="button"
                  onClick={openLocationDropdown}
                  className="flex items-center justify-between gap-2 border border-[var(--line)] rounded-md px-2 py-1.5 sm:px-3 text-sm bg-white w-16 sm:w-20 focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                >
                  <span className="truncate">{locationParameter || "-"}</span>
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    className={`shrink-0 transition-transform ${locationOpen ? "rotate-180" : ""}`}
                  >
                    <path
                      d="M2.5 4.5L6 8l3.5-3.5"
                      stroke="var(--muted)"
                      strokeWidth="1.4"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>

                {locationOpen &&
                  createPortal(
                    <ul
                      ref={locationListRef}
                      role="listbox"
                      style={{
                        position: "fixed",
                        top: locationPos.top,
                        left: locationPos.left,
                        width: Math.max(locationPos.width, 256),
                      }}
                      className="max-h-64 overflow-y-auto bg-white border border-[var(--line)] rounded-md shadow-lg z-50 py-1"
                    >
                      {(apiLocationList || []).map((loc) => (
                        <li
                          key={loc.loc_code}
                          role="option"
                          aria-selected={locationParameter === loc.loc_code}
                          onClick={() => handleLocationSelect(loc.loc_code)}
                          className={`px-3 py-1.5 text-sm cursor-pointer hover:bg-[var(--accent-soft)] ${
                            locationParameter === loc.loc_code ? "bg-[var(--accent-soft)] font-medium" : ""
                          }`}
                        >
                          {loc.loc_code} — {loc.DESC}
                        </li>
                      ))}
                    </ul>,
                    document.body
                  )}
              </div>

              {/* Apply Button */}
              <button
                onClick={handleApplyFilters}
                type="button"
                className="shrink-0 bg-[var(--ink)] text-white text-sm font-medium px-3 sm:px-4 py-1.5 rounded-md hover:opacity-90 transition"
              >
                Apply
              </button>
            </div>
          </div>

          <div className="bg-[var(--surface)] border border-[var(--line)] rounded-lg overflow-hidden">
            {loading && (
              <div className="px-4 py-10 text-center text-[var(--muted)] text-sm">
                Loading stock report…
              </div>
            )}

            {!loading && !error && !summary && (
              <div className="px-4 py-10 text-center text-[var(--muted)] text-sm">
                No summary available for this date and location. Change filters to run the report.
              </div>
            )}

            {!loading && summary && (
              <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-[var(--line)]">

<div className="px-4 py-4 sm:py-5">
                  <p className="text-xs font-medium text-[var(--muted)] uppercase tracking-wider mb-1">
                    Total Value
                  </p>
                  <p className="mono text-lg sm:text-xl font-semibold">
                    {formatCellValue(summary.Total_Value, { decimals: 5 })}
                  </p>
                </div>

                <div className="px-4 py-4 sm:py-5">
                  <p className="text-xs font-medium text-[var(--muted)] uppercase tracking-wider mb-1">
                    Total Qty
                  </p>
                  <p className="mono text-lg sm:text-xl font-semibold">
                    {formatCellValue(summary.Total_Qty, { decimals: 2 })}
                  </p>
                </div>
               
                <div className="px-4 py-4 sm:py-5">
                  <p className="text-xs font-medium text-[var(--muted)] uppercase tracking-wider mb-1">
                    SKU Count
                  </p>
                  <p className="mono text-lg sm:text-xl font-semibold">
                    {formatCellValue(summary.TotalRows, { decimals: 0 })}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
        {error && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-4 py-3">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}